import React, { useEffect, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { useObservable } from '@ngneat/react-rxjs';
import { useNavigate } from 'react-router-dom';
import { useEffectFn } from '@ngneat/effects-hooks';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { finalize, Observable } from 'rxjs';
import { GridValueGetterParams } from '@mui/x-data-grid-pro';
import { emitOnce } from '@ngneat/elf';

import { searchSuppliersEffect, Supplier, suppliersQuery, suppliersService } from '@store/suppliers';
import { GlobalStatus } from '@store/common/globalStatus.enum';

import SearchComponent from '@components/inputs/search/Search.component';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import NewTableComponent from '@components/table/NewTable.component';

import { ConfirmActionProps, OtherActionType } from '@utils/table/columnTypes/actions.columnType';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import ConfirmModal from '@components/modal/Confirm.modal';
import { suppliersUnitsService } from '@store/suppliers/units';
import { suppliersServicesService } from '@store/suppliers/services';
import { documentsService } from '@store/documents';

const SuppliersListTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const searchSuppliers = useEffectFn(searchSuppliersEffect);

  const [openConfirmAction, setOpenConfirmAction] = useState<ConfirmActionProps | false>(false);

  const [{ suppliers, loading }] = useObservable(suppliersQuery.suppliers$);
  const [selectedSuppliers] = useObservable(suppliersQuery.selectedSuppliers$);

  const [filterStatus] = useObservable(suppliersQuery.status$);
  const [filters] = useObservable(suppliersQuery.filters$);
  const [sortModel] = useObservable(suppliersQuery.sort$);
  const [tablePagination] = useObservable(suppliersQuery.pagination$);

  useEffect(() => {
    searchSuppliers({ status: filterStatus, filters, tablePagination, sort: sortModel });
  }, [filters, filterStatus, searchSuppliers, sortModel, tablePagination]);

  const handleConfirmValidation = (type: OtherActionType, supplier: Supplier) => (handleStopLoading: () => void) => {
    let validationObservable: Observable<any> | undefined = undefined;
    switch (type) {
      case OtherActionType.DELETE:
        validationObservable = suppliersService.deleteSupplierInList(supplier.id);
        break;
      case OtherActionType.ARCHIVE:
        validationObservable = suppliersService.updateSupplierStatusInList(supplier.id, GlobalStatus.ARCHIVED);
        break;
    }
    validationObservable?.pipe(finalize(handleStopLoading)).subscribe({
      next: () => {
        enqueueSnackbar(t(`${OtherActionType.translationKey(type)}.success.supplier`), { variant: 'success' });
        setOpenConfirmAction(false);
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  return (
    <Stack flex={1} spacing={2} pb="25px">
      <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
        <Box width="500px">
          <SearchComponent
            value={filters.search}
            handleChange={(value) => {
              emitOnce(() => {
                suppliersService.setFilters('search')(value);
                suppliersService.setPagination('page')(1);
              });
            }} />
        </Box>
        <TableFiltersComponent
          filters={filters}
          handleSave={suppliersService.setFilters('tableFilters')}
          handleReset={() => suppliersService.resetFilters()} />
      </Stack>
      <NewTableComponent
        loading={loading}
        columns={[
          COLUMN_TYPES.TEXT_BOLD({
            field: 'organisation.id',
            label: t('suppliers.table.id'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Supplier>) => params.row.id,
            width: 100
          }),
          COLUMN_TYPES.TEXT_BOLD({
            field: 'organisation.name',
            label: t('suppliers.table.name'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Supplier>) => params.row.name,
            fullWidth: true,
            minWidth: 350,
          }),
          COLUMN_TYPES.TEXT({
            field: 'email',
            color: 'secondary',
            label: t('suppliers.table.email'),
            fullWidth: true,
            minWidth: 300
          }),
          COLUMN_TYPES.TEXT({
            field: 'address.country',
            color: 'secondary',
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Supplier>) => params.row.country,
            label: t('suppliers.table.country'),
            width: 150
          }),
          COLUMN_TYPES.TEXT({
            field: 'organisation.createdAt',
            color: 'secondary',
            label: t('suppliers.table.createdAt'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Supplier>) => params.row.createdAt,
            valueFormatter: (params) => params.value ? t('global.date', { date: new Date(params.value) }) : '-',
            width: 130,
          }),
          COLUMN_TYPES.ACTIONS({
            handleShowRow: (supplier: Supplier) => {
              suppliersUnitsService.resetStore();
              suppliersServicesService.resetStore();
              documentsService.resetStore();
              navigate(`../${supplier.id}`);
            },
            otherActions: [
              {
                type: OtherActionType.ARCHIVE,
                handleClick: (supplier: Supplier) => setOpenConfirmAction({
                  type: OtherActionType.ARCHIVE,
                  row: supplier
                }),
                hidden: (supplier: Supplier) => supplier.status === GlobalStatus.ARCHIVED || filterStatus === GlobalStatus.ARCHIVED,
              },
              {
                type: OtherActionType.DELETE,
                handleClick: (supplier: Supplier) => setOpenConfirmAction({
                  type: OtherActionType.DELETE,
                  row: supplier
                }),
              },
            ]
          }),
        ]}
        handlePageSizeChange={suppliersService.setPagination('limit')}
        pageSize={tablePagination.limit}
        handlePageChange={(page) => suppliersService.setPagination('page')(page + 1)}
        page={tablePagination.page - 1}
        handleSortModelChange={suppliersService.setSort}
        sortModel={[sortModel]}
        handleRowSelected={suppliersService.setSelectedSuppliers}
        rowsSelected={selectedSuppliers}
        handleReload={() => searchSuppliers({ status: filterStatus, filters, tablePagination, sort: sortModel })}
        totalRows={suppliersQuery.totalRows}
        data={suppliers} />
      {!!openConfirmAction && (
        <ConfirmModal
          handleClose={() => setOpenConfirmAction(false)}
          description={t(`${OtherActionType.translationKey(openConfirmAction.type)}.description.supplier`)}
          handleValidate={handleConfirmValidation(openConfirmAction.type, openConfirmAction.row)}
          title={t(`${OtherActionType.translationKey(openConfirmAction.type)}.title`)}
          validateButton={OtherActionType.label(openConfirmAction.type)} />
      )}
    </Stack>
  );
};

export default SuppliersListTable;
